<template>
  <div class="wapers-corner-page container">
    <TheBreadcrumb :pages="arianeRoute" />
    <HeaderCommunity :page="currentPageInfo" />
    <SearchResults
      :is-loading="isLoading"
      placeholder-text="Rechercher un contenu"
      @search-update="searchUpdateHandler"
    >
      <template #title>
        <FilterSub
          :current="filterSubCategory"
          :items="communityWaperCornersSubcategoriesList"
          :action="(slug) => changeFilterTo(slug)"
          inside-header
        />
      </template>
      <template #results>
        <CardContainer
          :items="communityWaperCorners"
          :no-result-text="noContent"
        />
      </template>
    </SearchResults>
    <ThePagination
      :total-pages="communityWaperCornersPages"
      :current-page="communityWaperCornersCurrentPage"
      :go-to="changePageTo"
    />
    <PegiWarning v-if="community?.pegi === '18'" />
  </div>
</template>

<script setup lang="ts">
definePageMeta({
  middleware: async (to) => {
    const loadWapersCornerCommunity = useLoadWapersCornerCommunity()
    return loadWapersCornerCommunity(to)
  },
})

const waperCornersStore = useWaperCornersStore()
const {
  communityWaperCorners,
  communityWaperCornersPages,
  communityWaperCornersCurrentPage,
  communityWaperCornersSubcategoriesList,
} = storeToRefs(waperCornersStore)

const link = useLinkPrevNext({
  currentPage: communityWaperCornersCurrentPage,
  pagesCount: communityWaperCornersPages,
})

const communitiesStore = useCommunitiesStore()
const { community } = storeToRefs(communitiesStore)

const arianeRoute = computed(() => {
  return [
    {
      title: 'Toutes les communautés',
      route: '/communautes',
    },
    {
      title: community.value?.title,
      route: `/communautes/${community.value?.slug}`,
    },
    {
      title: 'Le feed des players',
      route: ``,
    },
  ]
})

const breadcrumbSchema = useBreadcrumbSchema({
  arianeRoute,
})

useHead({
  title: `Discussions et décryptages des Wapers sur ${community.value?.title}`,
  link,
  script: [breadcrumbSchema],
})

useSeoMeta({
  // nothing to list yet ("Aucun contenu"): kept out of Google, as out of the sitemap (CommunityController)
  robots: computed(() =>
    communityWaperCorners.value.length ||
    communityWaperCornersSubcategoriesList.value.length
      ? undefined
      : 'noindex, follow',
  ),
  title: `Discussions et décryptages des Wapers sur ${community.value?.title}`,
  description: `Venez discuter et échanger sur ${community.value?.title} : infos, avis, forum, challenges et vidéos. We Are PlayStation : Rejoignez la communauté.`,
  ogTitle: `Tout le feed des players sur ${community.value?.title}.`,
  ogDescription: `Découvrez toutes les discussions, avis et décryptages sur ${community.value?.title}.`,
  ogImage:
    community.value?.image_desktop?.url ??
    'https://www.weareplaystation.fr/img/og-img.jpg',
})

const route = useRoute()

const slug = computed(() => {
  return route.params.communityName as string
})

const referentialStore = useReferentialStore()
const { referential } = storeToRefs(referentialStore)

const currentPageInfo = computed(() => {
  const { image, imageMobile } = getImages(community)

  return {
    title: 'Le feed des players',
    game: community.value?.title,
    pegi: community.value?.pegi,
    members: community.value?.users_count,
    image,
    imageMobile,
    contribPoints: referential.value?.points.post_source_create,
    description: `Découvrez toutes les discussions, avis et décryptages sur ${community.value?.title}.`,
  }
})

const filterSubCategory = ref('all')

const searchData = async () => {
  isLoading.value = true

  const filter =
    filterSubCategory.value !== 'all' ? filterSubCategory.value : ''

  await waperCornersStore.fetchCommunityWaperCorners({
    page: 1,
    search: search.value,
    filter,
    community: slug.value,
  })

  if (!communityWaperCorners.value.length) {
    noContent.value = !search.value
      ? 'Aucun contenu'
      : 'Aucun résultat pour votre recherche'
  }

  isLoading.value = false
}

const { isLoading, search, searchUpdateHandler, noContent } = useSearching({
  searchData,
})

const changePageTo = async (index = 1) => {
  if (index > 0) {
    const filter =
      filterSubCategory.value !== 'all' ? filterSubCategory.value : ''

    await waperCornersStore.fetchCommunityWaperCorners({
      page: index,
      search: search.value,
      filter,
      community: slug.value,
    })
  }
}

const changeFilterTo = async (subCategory: string) => {
  filterSubCategory.value = subCategory
  const filter =
    filterSubCategory.value !== 'all' ? filterSubCategory.value : ''

  await waperCornersStore.fetchCommunityWaperCorners({
    page: 1,
    search: search.value,
    filter,
    community: slug.value,
  })
}

const loadWapersCornerCommunity = useLoadWapersCornerCommunity()
const { authenticated } = useAuthenticated()

watch(authenticated, (newValue) => {
  if (newValue) {
    loadWapersCornerCommunity(route)
  }
})
</script>

<style lang="scss" scoped>
.wapers-corner-page {
  min-height: 100vh;
  padding-bottom: 10rem;
}
</style>
